<template>
  <v-container fluid class="reports-container pa-0">
    <!-- Modern Header -->
    <div
      v-if="showHeader"
      class="header-section mb-4"
      style="margin: 0 12px; position: relative;"
    >
      <!-- Close Button -->
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-chart-line</v-icon>
        </div>
        <div class="flex-grow-1">
          <h1 class="page-title mb-1">Reports and Analytics</h1>
          <p class="page-subtitle mb-0">
            Master reports with advanced filtering and analytics
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex">
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-chart-box</v-icon>
            Analytics
          </v-chip>
          <v-chip
            v-if="!$store.state.user.permissions.disableExportList"
            class="white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-export</v-icon>
            Exportable
          </v-chip>
        </div>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0" justify="center">
        <v-col cols="6" sm="4" md="4" lg="2" class="pa-1">
          <div class="feature-pill customBorderRadius">
            <v-icon size="18" color="white" class="mr-2">mdi-filter</v-icon>
            <span>Advanced Filters</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="4" lg="2" class="pa-1">
          <div class="feature-pill customBorderRadius">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-chart-donut</v-icon
            >
            <span>Visual Charts</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="4" lg="2" class="pa-1">
          <div class="feature-pill customBorderRadius">
            <v-icon size="18" color="white" class="mr-2">mdi-table</v-icon>
            <span>Data Tables</span>
          </div>
        </v-col>
        <v-col cols="6" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill customBorderRadius">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-calendar-range</v-icon
            >
            <span>Date Range</span>
          </div>
        </v-col>
        <v-col v-if="!$store.state.user.permissions.disableExportList" cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill customBorderRadius">
            <v-icon size="18" color="white" class="mr-2">mdi-download</v-icon>
            <span>Export Data</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Action bar: Classic / Modern view toggle -->
    <div class="reports-action-bar px-3 mb-3">
      <button
        type="button"
        class="classic-view-toggle reports-classic-view-toggle"
        role="switch"
        aria-checked="false"
        @click="goToModernReports"
      >
        <span class="classic-view-toggle-label classic-view-classic-label"
          >Classic View</span
        >
        <span class="classic-view-switch" aria-hidden="true">
          <span class="classic-view-switch-thumb"></span>
        </span>
        <span class="classic-view-toggle-label classic-view-modern-label"
          >Modern View</span
        >
      </button>
    </div>

    <v-row class="ma-0 px-3">
      <v-flex xs12 sm12 md12>
        <v-card class="modern-card customBorderRadius" elevation="0">
          <div class="card-header-green"></div>
          <v-card-title class="pt-6 pb-4">
            <v-icon left color="primary">mdi-filter-variant</v-icon>
            <span class="text-h6 font-weight-medium">Filter Reports</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <v-row class="ma-0">
              <v-col cols="6" sm="4" md="2" lg="2">
                <v-autocomplete
                  v-model="selectedList"
                  :items="lists"
                  item-text="name"
                  item-value="id"
                  label="Lists"
                  autocomplete="false"
                  class="my-2"
                  hide-details
                  solo
                  flat
                  outlined
                  background-color="inverse"
                  multiple
                  dense
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" label x-small>
                      <span>{{ item.name }}</span>
                    </v-chip>
                    <span v-if="index === 1" class="grey--text caption">
                      (+{{ selectedList.length - 1 }} others)
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="6" sm="4" md="2" lg="2">
                <v-autocomplete
                  v-model="selectedUser"
                  :items="allUsers"
                  item-text="name"
                  item-value="id"
                  label="Members"
                  autocomplete="false"
                  class="my-2"
                  hide-details
                  solo
                  flat
                  outlined
                  background-color="inverse"
                  multiple
                  dense
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" label x-small>
                      <span>{{ item.name }}</span>
                    </v-chip>
                    <span v-if="index === 1" class="grey--text caption">
                      (+{{ selectedUser.length - 1 }} others)
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="6" sm="4" md="2" lg="2">
                <v-select
                  v-model="selectedDispos"
                  :items="calcDispo"
                  item-text="title"
                  item-value="title"
                  label="Dispo"
                  class="my-2"
                  hide-details
                  solo
                  flat
                  outlined
                  background-color="inverse"
                  multiple
                  dense
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" label x-small>
                      <span>{{ item.title }}</span>
                    </v-chip>
                    <span v-if="index === 1" class="grey--text caption">
                      (+{{ selectedDispos.length - 1 }} others)
                    </span>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="6" sm="4" md="2" lg="2">
                <v-select
                  v-model="selectedCallDirection"
                  :items="callDirections"
                  label="Call Direction"
                  class="my-2"
                  hide-details
                  solo
                  flat
                  outlined
                  background-color="inverse"
                  dense
                >
                </v-select>
              </v-col>

              <v-col cols="6" sm="8" md="2" lg="2">
                <v-dialog
                  ref="dialog"
                  v-model="modal"
                  :return-value.sync="dates"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="dateRange1"
                      label="Date Range"
                      prepend-inner-icon="event"
                      class="my-2"
                      hide-details
                      solo
                      flat
                      outlined
                      background-color="inverse"
                      readonly
                      dense
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dates"
                    scrollable
                    color="primarygrad"
                    range
                  >
                    <v-spacer></v-spacer>
                    <v-btn text @click="modal = false">Cancel</v-btn>
                    <v-btn
                      text
                      color="primarygrad"
                      @click="$refs.dialog.save(dates)"
                      >OK</v-btn
                    >
                  </v-date-picker>
                </v-dialog>
              </v-col>

              <v-col cols="6" sm="4" md="2" lg="2">
                <v-btn
                  color="primary"
                  depressed
                  large
                  style="border-radius: 8px; margin-top: 10px; height: 40px;"
                  block
                  :loading="preloader"
                  @click="load"
                >
                  <v-icon left>mdi-chart-line</v-icon>
                  Generate
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          v-if="preloader"
          class="modern-card customBorderRadius mt-4"
          elevation="0"
        >
          <v-card-text class="pa-6">
            <div style="text-align: center; padding: 50px;">
              <v-progress-circular
                :width="3"
                color="primary"
                indeterminate
              ></v-progress-circular>
              <p class="mt-4 text--secondary">Loading reports...</p>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="graphData.length > 0 && !preloader"
          class="modern-card customBorderRadius mt-4"
          elevation="0"
        >
          <div class="card-header-green"></div>
          <v-card-title class="pt-6 pb-4">
            <v-icon left color="primary">mdi-chart-box-outline</v-icon>
            <span class="text-h6 font-weight-medium">Analytics Overview</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <v-row>
              <v-col cols="12" md="4">
                <v-card class="chart-card customBorderRadius" elevation="0">
                  <v-card-title class="justify-center">
                    <h4 class="text-h6 font-weight-medium">
                      Disposition Summary
                    </h4>
                  </v-card-title>
                  <v-card-text>
                    <v-row
                      class="fill-height pt-2 pb-4"
                      align="center"
                      justify="center"
                    >
                      <doughnut-chart
                        class="text-align-center justify-content-center"
                        style="
                                              width: 100%;
                                              max-width: 300px;
                                              margin: 0 auto;
                                            "
                        :chart-data="datacollection"
                        :options="{
                          legend: {
                            position: 'bottom',
                            display: labelShown,
                          },
                        }"
                      ></doughnut-chart>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="4">
                <v-card class="chart-card customBorderRadius" elevation="0">
                  <v-card-title class="justify-center">
                    <h4 class="text-h6 font-weight-medium">Call Summary</h4>
                  </v-card-title>
                  <v-card-text>
                    <v-row
                      class="fill-height pt-2 pb-4"
                      align="center"
                      justify="center"
                    >
                      <bar-chart
                        class="text-align-center justify-content-center"
                        style="
                                              width: 100%;
                                              max-width: 300px;
                                              margin: 0 auto;
                                            "
                        :chart-data="barChartData"
                        :options="{
                          legend: { display: false },
                          scales: {
                            yAxes: [
                              {
                                gridLines: { display: false },
                                ticks: { beginAtZero: true },
                              },
                            ],
                            xAxes: [
                              {
                                gridLines: { display: false },
                              },
                            ],
                          },
                        }"
                      ></bar-chart>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="4">
                <v-card class="chart-card customBorderRadius" elevation="0">
                  <v-card-title class="justify-center">
                    <h4 class="text-h6 font-weight-medium">Contact Ratio</h4>
                  </v-card-title>
                  <v-card-text>
                    <v-row
                      class="fill-height pt-2 pb-4"
                      align="center"
                      justify="center"
                    >
                      <pie-chart
                        class="text-align-center justify-content-center"
                        style="
                                              width: 100%;
                                              max-width: 300px;
                                              margin: 0 auto;
                                            "
                        :chart-data="pieChartData"
                      ></pie-chart>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="modern-card customBorderRadius mt-4" elevation="0">
          <div class="card-header-green"></div>
          <v-card-title class="pt-6 pb-4">
            <v-row wrap class="ma-0 align-center" @keyup.enter="query">
              <v-flex xs12 sm12 md5 class="px-1 mb-2 mb-md-0">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="search"
                  clearable
                  outlined
                  dense
                  placeholder="Search reports..."
                  single-line
                  hide-details
                  @click:clear="searchReset()"
                ></v-text-field>
              </v-flex>

              <v-flex xs6 sm4 md2 class="px-1 mb-2 mb-md-0">
                <v-autocomplete
                  v-model="searchField"
                  :items="fieldsList"
                  item-value="value"
                  item-text="name"
                  autocomplete="false"
                  hide-details
                  label="Fields"
                  outlined
                  dense
                ></v-autocomplete>
              </v-flex>

              <v-flex xs6 sm4 md2 class="px-1 mb-2 mb-md-0">
                <v-btn
                  color="primary"
                  depressed
                  large
                  style="border-radius: 8px;"
                  block
                  @click="query"
                >
                  <v-icon left>search</v-icon>
                  Search
                </v-btn>
              </v-flex>

              <v-flex
                v-if="!$store.state.user.permissions.disableExportList"
                xs6
                sm4
                md2
                class="px-1 mb-2 mb-md-0"
                style="text-align: center;"
              >
                <v-btn
                  color="primary"
                  outlined
                  large
                  style="border-radius: 8px;"
                  block
                  @click="exportReport"
                >
                  <v-icon left size="18">save_alt</v-icon>Export
                </v-btn>
              </v-flex>
              <v-flex
                xs6
                sm12
                md1
                class="px-1 d-flex justify-center justify-sm-end justify-md-center"
              >
                <v-menu
                  transition="slide-y-transition"
                  fixed
                  bottom
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      v-bind="attrs"
                      label
                      style="height:40px;width:80px;justify-content: center;"
                      v-on="on"
                    >
                      <v-icon left color="primary">view_module</v-icon>

                      <v-icon color="primary">mdi-chevron-down</v-icon>
                    </v-chip>
                  </template>
                  <v-list style="max-height: 300px; overflow-y:scroll">
                    <v-list-item-group>
                      <v-list-item v-for="item in allHeaders" :key="item.value">
                        <v-list-item-content>
                          <v-list-item-title
                            style="font-weight: 500; font-size: 15px"
                            v-text="item.text"
                          ></v-list-item-title>
                        </v-list-item-content>

                        <v-list-item-action>
                          <v-checkbox
                            :input-value="isHeaderSelected(item.value)"
                            ripple
                            :disabled="
                              item.value == 'phone' || item.value == 'name'
                            "
                            style="margin-top: -3px"
                            color="primary"
                            @change="toggleHeader(item, $event)"
                          ></v-checkbox>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-menu>
              </v-flex>
            </v-row>
          </v-card-title>

          <v-data-table
            ref="reportsDataTable"
            :headers="reportHeaders"
            :items="contactDetails"
            :items-per-page="records"
            hide-default-footer
            :loading="loading"
            :loading-text="loadingText"
          >
            <!-- <template v-slot:headers="{ reportHeaders }">
              <v-icon v-if="reportHeaders.icon" left>{{
                reportHeaders.icon
              }}</v-icon>
              {{ reportHeaders.text }}
            </template> -->

            <!-- <template v-slot:header.Duration="{ header }">
              <v-icon small>plus-circle-outline</v-icon>{{ header.text }}
            </template> -->

            <template v-slot:header.Duration="{ header }">
              <span style="display: inline-flex">
                {{ header.text }}

                <v-tooltip top max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-bind="attrs" v-on="on">info</v-icon>
                  </template>
                  <span>Total amount of Talk Time for every call</span>
                </v-tooltip>
              </span>
            </template>

            <template v-slot:header.TimeSpent="{ header }">
              <span style="display: inline-flex">
                {{ header.text }}

                <v-tooltip top max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-bind="attrs" v-on="on">info</v-icon>
                  </template>
                  <span
                    >Amount of Time spent on every contact, includes : ringing
                    time, disposition set time etc</span
                  >
                </v-tooltip>
              </span>
            </template>

            <template v-slot:[`item.dispo`]="{ item }">
              <div>
                <h5
                  v-if="item.dispo == 'CALLBACK'"
                  style="font-size: 14px; font-weight: 400"
                >
                  {{ item.dispo }} ({{
                    $moment(item.callbackOn).format("lll")
                  }})
                </h5>
                <h5 v-else style="font-size: 14px; font-weight: 400">
                  {{ item.dispo }}
                </h5>
              </div>
            </template>

            <template v-slot:[`item.Duration`]="{ item }">
              <span v-if="item.type == 'call'">
                <div v-if="parseInt(item.Duration) > 0">
                  <span style="padding-left: 6px;">
                    <v-chip small class="pointer" color="#2ecc71" label>
                      {{ item.Duration }}
                    </v-chip></span
                  >
                </div>
                <div v-else>
                  <span style="padding-left: 6px;">
                    <v-chip small class="pointer" color="#FF6384" label>
                      {{ item.Duration }}
                    </v-chip>
                  </span>
                </div>
              </span>
            </template>

            <template v-slot:[`item.phone`]="{ item }">
              <span v-if="item.type == 'call'">
                <div
                  v-if="item.direction == 'incoming'"
                  style="display: inline-flex"
                >
                  {{ item.phone }}
                  <v-icon style="padding-left: 6px;">mdi-phone-incoming</v-icon>
                </div>

                <div v-else style="display: inline-flex">
                  {{ item.phone }}
                  <v-icon style="padding-left: 6px;">mdi-phone-outgoing</v-icon>
                </div>
              </span>

              <span v-else>
                <div v-if="item.type == 'sms'" style="display: inline-flex">
                  {{ item.phone }}
                  <v-icon style="padding-left: 6px;">sms</v-icon>
                </div>

                <div v-if="item.type == 'email'" style="display: inline-flex">
                  {{ item.phone }}
                  <v-icon style="padding-left: 6px;">mdi-email</v-icon>
                </div>

                <div v-if="item.type == 'link'" style="display: inline-flex">
                  {{ item.phone }}
                  <v-icon style="padding-left: 6px;">mdi-link</v-icon>
                </div>

                <div v-if="item.type == 'app'" style="display: inline-flex">
                  {{ item.phone }}
                  <v-icon style="padding-left: 6px;">mdi-apps</v-icon>
                </div>
              </span>
            </template>

            <template v-slot:[`item.secondPhone`]="{ item }">
              <span>{{ item.secondPhone || "-" }}</span>
            </template>

            <template v-slot:[`item.recording.name`]="{ item }">
              <div v-if="item.defaultDialer == 'Exotel'">
                <!-- <v-btn
                  text
                  fab
                  small
                  :href="item.serverResponse.RecordingUrl"
                  target="_blank"
                >
                  <v-icon style="opacity: 0.6" small>play_arrow</v-icon>
                </v-btn> -->
                <v-btn text fab small @click="openDialog(item, 'exotel')">
                  <v-icon style="opacity: 0.6" small>play_arrow</v-icon>
                </v-btn>
              </div>
              <div
                v-else-if="
                  item.defaultDialer == 'Telecmi' ||
                    item.defaultDialer == 'Telecmi-Linphone'
                "
              >
                <v-btn
                  text
                  fab
                  small
                  @click="openDialogRecordPopup(item, 'telecmi')"
                >
                  <v-icon style="opacity: 0.6" small>play_arrow</v-icon>
                </v-btn>
              </div>

              <div
                v-else-if="
                  item.exotel && item.defaultDialer == 'Phone Dialer with Rec'
                "
              >
                <v-btn
                  text
                  fab
                  small
                  @click="
                    download(item.exotel.godialRecordingUrl, 'exotel-rec')
                  "
                >
                  <v-icon style="opacity: 0.6" small>cloud_download</v-icon>
                </v-btn>
              </div>

              <!-- SIP Call Recording - lazy load on click -->
              <div v-else-if="item.sipCallId && item.truecallerCallRecording">
                <v-btn text fab small @click="openSipRecording(item)">
                  <v-icon style="opacity: 0.6" small>play_arrow</v-icon>
                </v-btn>
              </div>

              <div v-else-if="item.recording && item.recording.success">
                <v-btn
                  text
                  fab
                  small
                  @click="openDialogRecordPopup(item, 'phone')"
                >
                  <v-icon style="opacity: 0.6" small>play_arrow</v-icon>
                </v-btn>
              </div>
              <div v-else>
                Missing
              </div>
            </template>
          </v-data-table>

          <v-divider style="margin-bottom: 10px"></v-divider>
          <v-card-actions class="d-flex align-center" style="padding: 16px">
            <!-- Scroll Controls -->
            <div class="scroll-controls d-flex align-center">
              <v-btn
                icon
                small
                class="scroll-btn mr-1"
                title="Scroll Left"
                @click="scrollReportsTable('left')"
              >
                <v-icon color="#37cf37">mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                class="scroll-btn"
                title="Scroll Right"
                @click="scrollReportsTable('right')"
              >
                <v-icon color="#37cf37">mdi-chevron-right</v-icon>
              </v-btn>
            </div>
            <v-spacer></v-spacer>
            <div class="v-data-footer d-flex align-center">
              <div class="v-data-footer__select d-flex align-center">
                <span style="margin-right: 8px">Rows per page:</span>
                <v-select
                  v-model="records"
                  :items="recordsPerPage"
                  hide-details
                  dense
                  solo
                  flat
                  style="max-width: 80px"
                  @change="pagination"
                ></v-select>
              </div>
              <div class="v-data-footer__icons-before">
                <v-btn
                  :disabled="page == 1 ? true : false"
                  color="primary"
                  fab
                  small
                  text
                  @click="prevSet"
                >
                  <v-icon :size="24">arrow_left</v-icon>
                </v-btn>
              </div>
              <div class="v-data-footer__pagination">
                {{ `${page}/${pageCount} of ${totalRecords}` }}
              </div>
              <div class="v-data-footer__icons-after">
                <v-btn
                  :disabled="page >= pageCount ? true : false"
                  color="primary"
                  fab
                  small
                  text
                  @click="nextSet"
                >
                  <v-icon :size="24">arrow_right</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-row>

    <!-- Loader -->
    <v-dialog v-model="loader" persistent width="300">
      <v-card color="primary white--text" dark>
        <v-card-text style="padding: 18px 25px">
          <h3 style="margin-bottom: 5px; font-weight: 400; font-size: 16px">
            Please Wait...
          </h3>
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loader1" persistent width="300">
      <v-card color="primary white--text" dark>
        <v-card-text style="padding: 18px 25px">
          <h3 style="margin-bottom: 5px; font-weight: 400; font-size: 16px">
            Exporting: {{ displayExportProgress }}
          </h3>
          <v-progress-linear
            :value="exportProgress"
            color="white"
            max="130"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogReportsExports" width="900">
      <v-card>
        <v-card-title class="primarygrad">
          Export Report
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogReportsExports = false">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mt-3">
          <v-row class="pa-0 ma-0">
            <v-col xs12 sm12 md12 style="text-align: center; padding: 5px">
              <v-dialog
                ref="dialog"
                v-model="modal"
                :return-value.sync="dates"
                persistent
                width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateRange"
                    label="Date Range"
                    prepend-inner-icon="event"
                    class="my-2"
                    hide-details="true"
                    solo
                    flat
                    outlined
                    background-color="inverse"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dates"
                  scrollable
                  color="primarygrad"
                  range
                >
                  <v-spacer></v-spacer>
                  <v-btn text @click="modal = false">Cancel</v-btn>
                  <v-btn
                    text
                    color="primarygrad"
                    @click="$refs.dialog.save(dates)"
                    >OK</v-btn
                  >
                </v-date-picker>
              </v-dialog>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text color="red" @click="dialogReportsExports = false">
            Cancel
          </v-btn>
          <v-btn depressed color="primarygrad" @click="saveExportReports">
            Export
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" multi-line :color="color">
      <h3 style="font-weight: 400; font-size: 13px; text-transform: uppercase">
        {{ snackbarText }}
      </h3>

      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <record-popup ref="recordPopup"></record-popup>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
const _ = require("lodash");
import Papa from "papaparse";
import Blob from "blob";
import fb from "../js/firebase";
import DoughnutChart from "../js/doughnut.js";
import BarChart from "../js/bar.js";
import PieChart from "../js/pie.js";
import moment from "moment-timezone";
import * as XLSX from "xlsx/xlsx.mjs";
import recordPopup from "../components/recordPopup.vue";

export default {
  components: {
    DoughnutChart,
    BarChart,
    PieChart,
    recordPopup,
  },
  data() {
    return {
      deletedListIds: [],
      showHeader: false,
      exportProgress: 0,
      currentBatch: 0,
      totalBatches: 0,
      dialogReportsExports: false,
      telecmiIntegration: false,
      snackbar: false,
      snackbarText: "",
      color: "green",
      defaultDialer: "",
      audioDialog: false,
      selectedAudio: null,
      selectedLog: null,
      isPlaying: false,
      playbackTime: 0,
      selectedCallDirection: "all",
      callDirections: [
        { text: "All", value: "all" },
        { text: "Incoming", value: "incoming" },
        { text: "Outgoing", value: "outgoing" },
      ],
      // teams: [],
      labelShown: true,
      totalCallCount: 0,
      totalCallDuration: "",
      barChartData: {},
      pieChartData: {},
      statistics: [],
      datacollection: {},
      // pagination structure
      loader: false,
      loader1: false,
      recordsPerPage: [5, 15, 30, 60, 120, 500],
      page: 1,
      pageCount: 0,
      totalRecords: 0,
      records: 5,
      selectedUser: [],
      selectedList: [],
      lists: [],
      templates: [],
      preloader: false,
      search: "",
      contactDetails: [],
      loadChart: false,
      allUsers: [],
      allHeaders: [
        {
          text: "Name",
          align: "left",
          sortable: false,
          value: "name",
        },
        { text: "Phone", align: "left", sortable: false, value: "phone" },
        {
          text: "Secondary Phone",
          align: "left",
          sortable: false,
          value: "secondPhone",
        },
        {
          text: "Type",
          align: "left",
          sortable: false,
          value: "type",
        },
        {
          text: "Date",
          align: "left",
          sortable: false,
          value: "date",
        },
        {
          text: "Time",
          align: "left",
          sortable: false,
          value: "time",
        },
        {
          text: "Duration",
          align: "left",
          sortable: false,
          value: "Duration",
        },
        {
          text: "Time Spent",
          align: "left",
          sortable: false,
          value: "TimeSpent",
        },
        {
          text: "Dialer",
          align: "left",
          sortable: false,
          value: "defaultDialer",
        },
        {
          text: "Called/Received By",
          align: "left",
          sortable: false,
          value: "accounts.name",
        },
        {
          text: "Disposition",
          align: "left",
          sortable: false,
          value: "dispo",
        },
        {
          text: "Remarks",
          align: "left",
          sortable: false,
          value: "remarks",
        },
        {
          text: "Notes",
          align: "left",
          sortable: false,
          value: "note",
        },
        {
          text: "List",
          align: "left",
          sortable: false,
          value: "list.name",
        },
        {
          text: "Team",
          align: "left",
          sortable: false,
          value: "list.teams.name",
        },
        {
          text: "Template",
          align: "left",
          sortable: false,
          value: "template.title",
        },
        {
          text: "Recording",
          align: "left",
          sortable: false,
          value: "recording.name",
        },
        {
          text: "Call Status",
          align: "left",
          sortable: true,
          value: "callStatus",
        },
      ],
      modal: false,
      dates: [],
      dispos: [],
      selectedDispos: [],
      graphData: [],
      loading: false,
      loadingText: "",
      searchField: "",
      fieldsList: [
        { name: "Name", value: "name" },
        { name: "Phone", value: "phone" },
        { name: "Secondary Phone", value: "secondPhone" },
        { name: "Remarks", value: "remarks" },
        { name: "Note", value: "note" },
      ],
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "user", "fieldList", "teams", "companyDetails"]),
    reportHeaders: {
      get: function() {
        return this.$store.getters.reportHeaders;
      },
      set: function(newValue) {
        this.$store.commit("setReportHeaders", newValue);
      },
    },
    dateRange() {
      return this.dates.join(" - ");
    },
    dateRange1() {
      return this.dates.join(" - ");
    },
    dispoList() {
      return this.$store.getters.dispoList;
    },
    noReports() {
      return require("../assets/noReports.png");
    },
    selectedTeam: {
      get(v) {
        return this.$store.getters.selectedTeam;
      },
      set(v) {
        this.$store.commit("setSelectedTeam", v);
      },
    },
    calcDispo() {
      return _.without(
        _.map(this.dispos, (d) => {
          if (
            d.title == "SMS" ||
            d.title == "LINK" ||
            d.title == "EMAIL" ||
            d.title == "APP" ||
            d.title == "NEW"
          ) {
            //do not pass these values
          } else {
            return d;
          }
        }),
        undefined,
      );
    },
    displayExportProgress() {
      if (this.exportProgress <= 100) {
        return `Batch ${this.currentBatch}/${this.totalBatches} ( ${this.exportProgress}% )`;
      } else {
        return `Merging files: ${this.exportProgress - 100}%`;
      }
    },
    paginate() {
      return this.$store.getters.paginate;
    },
  },
  watch: {
    async selectedTeam() {
      try {
        await this.refresh(); // get accounts and lists and dispos
        await this.load(); // get the logs and load charts
      } catch (e) {
        console.log(e);
      }
    },
  },
  async mounted() {
    // Graphs
    // this.fillBarData();
    // this.fillData();
  },
  async created() {
    // Check if header was dismissed
    this.showHeader = localStorage.getItem("reportsHeaderDismissed") !== "true";
    try {
      // Paginate
      var routeName = this.$router.currentRoute.name
        .toString()
        .trim()
        .replace(/ /g, "")
        .toLowerCase();
      if (this.paginate[routeName]) {
        this.page = this.paginate[routeName].page;
        (this.pageCount = this.paginate[routeName].pageCount),
          (this.totalRecords = this.paginate[routeName].totalRecords),
          (this.records = this.paginate[routeName].records);
      }

      await this.refresh(); // get accounts and lists and dispos
      await this.fetchDeletedListIds();
      await this.load(); // get the logs and load charts
      this.getTelecmiDetails();
    } catch (e) {
      console.log(e);
    }
  },
  methods: {
    // Scroll table horizontally
    scrollReportsTable(direction) {
      const dataTable = this.$refs.reportsDataTable;
      const tableWrapper =
        dataTable &&
        dataTable.$el &&
        dataTable.$el.querySelector(".v-data-table__wrapper");
      if (tableWrapper) {
        const scrollAmount = 200;
        if (direction === "left") {
          tableWrapper.scrollBy({ left: -scrollAmount, behavior: "smooth" });
        } else {
          tableWrapper.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
      }
    },
    isHeaderSelected(value) {
      return this.reportHeaders.some((h) => h.value === value);
    },
    toggleHeader(item, checked) {
      let headers = [...this.reportHeaders];
      if (checked) {
        // Add the header if not already present
        if (!headers.some((h) => h.value === item.value)) {
          headers.push(item);
        }
      } else {
        // Remove the header
        headers = headers.filter((h) => h.value !== item.value);
      }
      this.reportHeaders = headers;
    },
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("reportsHeaderDismissed", "true");
    },
    goToModernReports() {
      this.$router.push({ path: "/reports-v2" });
    },
    async getTelecmiDetails() {
      var resp = await this.$http.get(
        `${this.ENDPOINT}/integrations?filter[where][companyId]=${this.companyDetails.id}&filter[where][type]=telecmi`,
      );

      if (resp.body.length > 0) this.telecmiIntegration = resp.body[0];
    },
    openDialog(item, dialingOpt) {
      console.log("item 112233", item);

      console.log("dialingOpt 11223344", dialingOpt);

      if (dialingOpt == "exotel") {
        (this.selectedAudio = null), (this.selectedLog = null);
        if (typeof item.serverResponse.RecordingUrl != "undefined") {
          var audioExotel = new Audio(`${item.serverResponse.RecordingUrl}`);
          this.selectedAudio = audioExotel;

          console.log("item 11111111111111111111111111111", item);

          this.selectedLog = item;
          this.audioDialog = true;
        }
      } else {
        (this.selectedAudio = null), (this.selectedLog = null);
        if (typeof item.recording != "undefined") {
          var audioPhone = new Audio(
            `${this.$store.state.ENDPOINT}/storages/get/${item.recording.name}?access_token=${this.$store.getters.user.token}`,
          );
          this.selectedAudio = audioPhone;
          this.selectedLog = item;
          this.audioDialog = true;
        }
      }
    },

    closeDialog() {
      this.selectedAudio.pause();
      // reset item var
      (this.isPlaying = false),
        (this.playbackTime = 0),
        (this.selectedAudio = null),
        (this.selectedLog = null),
        (this.audioDialog = false);
    },

    openDialogRecordPopup(item, dialingOpt) {
      console.log("item in open dialog", item);

      console.log("item in dialingOpt", dialingOpt);

      if (dialingOpt == "exotel") {
        (this.selectedAudio = null), (this.selectedLog = null);
        if (typeof item.serverResponse.RecordingUrl != "undefined") {
          var audioExotel = new Audio(`${item.serverResponse.RecordingUrl}`);
          this.selectedAudio = audioExotel;

          this.selectedLog = item;
          this.audioDialog = true;
        }
      }
      // else if (dialingOpt == "exotel-rec") {
      //   (this.selectedAudio = null), (this.selectedLog = null);
      //   if (typeof item.exotel.recordingUrl != "undefined") {
      //     var audioExotel2 = new Audio(`${item.exotel.recordingUrl}`);
      //     this.selectedAudio = audioExotel2;

      //     this.selectedLog = item;
      //     this.audioDialog = true;
      //   }
      // }
      else {
        console.log("this.$refs", this.$refs);
        this.$refs.recordPopup.openDialog(item, dialingOpt);
      }
    },

    closeDialogRecordPopup() {
      this.$refs.recordPopup.closeDialog(false);
    },

    async openSipRecording(item) {
      try {
        // Debug: log the entire item to see what fields are available
        console.log("🔍 Full item object:", JSON.stringify(item, null, 2));
        console.log("📞 SIP Call ID (sipCallId):", item.sipCallId);

        // Check if we already have the recording URL in truecallerCallRecording
        if (item.truecallerCallRecording && item.truecallerCallRecording.url) {
          console.log(
            "✅ Using existing recording URL from truecallerCallRecording",
          );

          // Add sipCallRecording to item for recordPopup
          item.sipCallRecording = {
            mergedUrl: item.truecallerCallRecording.url,
          };

          // Now open the dialog with the loaded data
          this.$refs.recordPopup.openDialog(item, "sip");
          return;
        }

        // Fallback: Try to fetch by sipCallId if truecallerCallRecording doesn't have URL
        const callId = item.sipCallId;

        if (!callId) {
          console.error("❌ No sipCallId found in item!");
          this.snackbarText = "Call ID not found for this recording.";
          this.color = "error";
          this.snackbar = true;
          return;
        }

        console.log("✅ Fetching recording by Call-ID:", callId);

        // Fetch recording by callId or uacCallId (for B2BUA scenarios)
        const response = await this.$http.get(
          `${
            this.$store.state.ENDPOINT
          }/sip-call-recordings?filter[where][or][0][callId]=${encodeURIComponent(
            callId,
          )}&filter[where][or][1][uacCallId]=${encodeURIComponent(
            callId,
          )}&access_token=${this.$store.getters.user.token}`,
        );

        if (response.data && response.data.length > 0) {
          const recording = response.data[0];
          console.log("✅ Found SIP recording:", recording);

          // Add sipCallRecording to item for recordPopup
          item.sipCallRecording = {
            mergedUrl: recording.gcsUrl || recording.mergedUrl,
            legAUrl: recording.legAUrl,
            legBUrl: recording.legBUrl,
          };

          // Now open the dialog with the loaded data
          this.$refs.recordPopup.openDialog(item, "sip");
        } else {
          // Recording not found or still processing
          this.snackbarText =
            "Call recording is still processing or not available yet.";
          this.color = "warning";
          this.snackbar = true;
        }
      } catch (error) {
        console.error("Error fetching SIP recording:", error);
        this.snackbarText = "Failed to load call recording.";
        this.color = "error";
        this.snackbar = true;
      }
    },

    download(src, dialingOpt) {
      if (src != "") {
        if (dialingOpt == "exotel") {
          var a = document.createElement("a");

          a.href = src;
          a.setAttribute("download", src.toString());
          a.target = "_blank";
          a.click();
        } else if (dialingOpt == "exotel-rec") {
          var url3 = src;

          console.log("url3", url3);

          var d3 = document.createElement("a");

          d3.href = url3;
          d3.setAttribute("download", url3.toString());
          d3.target = "_blank";
          d3.click();
        } else {
          var au = document.createElement("a");

          au.href = `${this.$store.state.ENDPOINT}/storages/get/${src}?access_token=${this.$store.getters.user.token}`;
          au.setAttribute("download", src.toString());
          au.target = "_blank";
          au.click();
        }
      }
    },
    audio(what) {
      const self = this;
      var audio = self.selectedAudio;
      if (typeof audio != "undefined") {
        switch (what) {
          case "start":
            self.isPlaying = true;
            audio.play();

            break;

          case "pause":
            // access audio from ongoing selected track
            audio.pause();
            // reset item var
            self.isPlaying = false;
            // self.playbackTime = 0;
            break;
        }

        audio.ontimeupdate = function(e) {
          self.playbackTime = (audio.currentTime / audio.duration) * 100;
          if (self.playbackTime >= 100 || isNaN(self.playbackTime)) {
            self.isPlaying = false;
            self.playbackTime = 0;
          }
        };
      }
    },

    /** SEARCH */
    async query() {
      try {
        if (this.search != "" && this.searchField != "") {
          this.loading = true;
          this.loadingText = "Loading... Please wait";
          this.page = 1;
        }
        this.fetchCount();
        this.contactDetails = await this.fetchLogs(
          this.records,
          this.records * this.page - this.records,
        );
      } catch (e) {
        // error
      }
    },
    searchReset() {
      this.searchField = "name";
      this.search = "";
      this.query();
    },

    /** PAGINATION */
    nextSet() {
      this.page++;
      this.pagination();
    },
    prevSet() {
      this.page--;
      this.pagination();
    },
    async pagination() {
      this.pageCount = Math.ceil(this.totalRecords / this.records);
      if (this.page > this.pageCount) {
        this.page = 1; //set default
      }

      this.contactDetails = await this.fetchLogs(
        this.records,
        this.records * this.page - this.records,
      );

      // Save default pagination of seperate pages
      this.paginate[
        this.$router.currentRoute.name
          .toString()
          .trim()
          .replace(/ /g, "")
          .toLowerCase()
      ] = {
        page: this.page,
        pageCount: this.pageCount,
        records: this.records,
        totalRecords: this.totalRecords,
      };
      this.$store.commit("setPagination", this.paginate);
      return;
    },

    /** INIT CODE */
    // Load the details

    async load() {
      this.page = 1;
      this.contactDetails = [];
      this.statistics = [];
      this.graphData = [];

      if (this.page > this.pageCount) {
        this.page = 1;
      }
      // date range limit
      var start = moment(this.dates[0]);
      var end = this.dates[1] == undefined ? start : moment(this.dates[1]);
      if (start > end) {
        var temp = start;
        start = end;
        end = temp;
      }
      var diff = end.diff(start, "days") + 1;

      // if range is greater than 30 days
      if (diff > 31) {
        this.loading = false;
        this.loadingText = "";
        this.snackbar = true;
        this.snackbarText = "Please select a range of 30 days";
        this.color = "#cc9900";
        return;
      }

      // // if select more than 5 lists
      if (this.selectedList.length > 5) {
        this.loading = false;
        this.loadingText = "";
        this.snackbar = true;
        this.snackbarText =
          " you can only select a maximum of five lists at a time. Please deselect some lists before continuing.";
        this.color = "#cc9900";
        return;
      }

      this.fetchGraphReports(); // graphical report
      this.fetchCount(); // Total log count
      this.contactDetails = await this.fetchLogs(
        this.records,
        this.records * this.page - this.records,
      ); // logs by limit and skip

      // Set field data
      if (this.fieldsList.length > 0 && this.searchField == "") {
        this.searchField = this.fieldsList[0].value;
      }
    },

    // load info for filter
    async refresh() {
      try {
        await this.fetchLists();
        await this.fetchAccounts();
        await this.fetchDispos();
        await this.fetchTemplates();
      } catch (e) {
        // error
      }
    },

    /** DELETE APIIS */
    deleteItem(item) {
      const self = this;
      self.$http.delete(self.ENDPOINT + "/logs/" + item.id).then(function() {
        self.fetchLogs();
      });
    },

    /** GET ARRAY OF list, user ids */
    getListIds() {
      var lists = [];
      if (_.includes(this.selectedList, "allLists")) {
        // lists = [];
        lists = _.map(this.lists, "id");
        if (this.deletedListIds && this.deletedListIds.length > 0) {
          lists = lists.concat(this.deletedListIds);
        }
      } else {
        lists = this.selectedList;
      }
      lists = _.without(lists, "allLists");
      return lists;
    },
    getTemplateIds() {
      var templates = [];
      templates = _.map(this.templates, "id");
      return templates;
    },
    getUserIds() {
      var users = [];

      if (
        _.includes(this.selectedUser, "allMembers") &&
        this.$store.getters.user.role.toLowerCase() == "manager"
      ) {
        users = [];
      } else if (
        _.includes(this.selectedUser, "allMembers") &&
        this.$store.getters.user.role.toLowerCase() == "teammanager"
      ) {
        users = _.map(this.allUsers, "id");
      } else {
        users = this.selectedUser;
      }
      users = _.without(users, "allMembers");
      return users;
    },

    allListReports() {
      this.dialogReportsExports = true;
    },

    async saveExportReports() {
      var self = this;

      // date range limit
      var start = moment(this.dates[0]);
      var end = this.dates[1] == undefined ? start : moment(this.dates[1]);
      if (start > end) {
        var temp = start;
        start = end;
        end = temp;
      }
      var diff = end.diff(start, "days") + 1;

      // if range is greater than 30 days
      if (diff > 31) {
        this.loading = false;
        this.loadingText = "";
        this.snackbar = true;
        this.snackbarText = "Please select a range of 30 days";
        this.color = "#cc9900";
        return;
      }

      if (this.dates.length < 1) {
        this.dates = [
          moment()
            .subtract(7, "days")
            .format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ];
      }

      var lists = _.map(this.lists, "id");

      lists = _.without(lists, "allLists");
      if (this.deletedListIds && this.deletedListIds.length > 0) {
        lists = lists.concat(this.deletedListIds);
      }

      const searchParamsForCount = {
        search: "",
        field: "",
        range: this.dates,
        lists: lists,
        users: [],
        dispos: [],
        count: true,
      };

      const responseCount = await this.$http.post(
        this.ENDPOINT + "/logs/byFilter",
        searchParamsForCount,
      );

      console.log("response of count", responseCount);

      var dataResultFinal = [];

      await Promise.all(
        lists.map(async (c) => {
          var searchParams = {
            search: "",
            field: "",
            range: this.dates,
            lists: [c],
            users: [],
            dispos: [],
            count: false,
          };

          var response = await this.$http.post(
            this.ENDPOINT + "/logs/byFilter",
            searchParams,
          );

          console.log("response body", response.body.data);

          if (response.body.data.length > 0) {
            dataResultFinal.push(...response.body.data);
          }
        }),
      );

      console.log("data", dataResultFinal);

      // const searchParams = {
      //   search: "",
      //   field: "",
      //   range: this.dates,
      //   lists: lists,
      //   users: [],
      //   dispos: [],
      //   limit: 5,
      //   skip: 0,
      //   count: false,
      // };
      // const data = [];
      // const result = await this.fetchData(searchParams, data, responseCount);
      // console.log(result);

      // if (typeof result != "undefined") {
      //   data.push(result);
      // }

      var dataResult = _.map(dataResultFinal, (r) => {
        // accounts
        var account = _.find(this.allUsers, (user) => {
          return r.accountsId == user.id;
        });
        if (account) {
          r.accounts = account;
        }

        //lists
        var list = _.find(this.lists, (list) => {
          return r.listId == list.id;
        });
        if (list) {
          r.list = list;
        }

        //templates
        var template = _.find(this.templates, (template) => {
          return r.templateId == template.id;
        });
        if (template) {
          r.template = template;
        }

        return r;
      });

      console.log("dataResult", dataResult);

      var exportData = [];

      exportData = _.orderBy(dataResult, "time", "desc");

      console.log("exportData", exportData);

      if (exportData.length < 1) {
        this.loader = false;
        this.$swal({ tyep: "warning", text: "No records to export" });
        return;
      }

      exportData = _.map(exportData, (d) => {
        var y = {
          Phone: d.phone.toString(),
        };

        if (typeof d.name != "undefined") {
          y.Name = d.name;
        } else {
          y.Name = "";
        }

        if (typeof d.companyName != "undefined") {
          y.Company = d.companyName;
        } else {
          y.Company = "";
        }

        if (d.calledOn) {
          y.Date = this.$moment(d.calledOn).format("YYYY-MM-DD");
          y.Time = this.$moment(d.calledOn).format("hh:mm a");
        } else {
          y.Date = "";
          y.Time = "";
        }

        if (d.leadScore) {
          y.Leadscore = d.leadScore;
        } else {
          y.Leadscore = "";
        }

        if (d.remarks) {
          y.Remarks = d.remarks;
        } else {
          y.Remarks = "";
        }

        if (d.extra) {
          y.Extra = d.extra;
        } else {
          y.Extra = "";
        }

        if (d.accounts && d.accounts.name) {
          y.CalledBy = d.accounts.name;
        } else {
          y.CalledBy = "";
        }

        if (d.direction) {
          y.Direction = d.direction;
        } else {
          y.Direction = "outgoing";
        }

        if (typeof d.list == "undefined" || d.list.name == "") {
          y.List = "";
        } else {
          y.List = d.list.name;
        }

        if (d.dispo) {
          y.Disposition = d.dispo;
        } else {
          y.Disposition = "";
        }

        if (d.callbackOn) {
          y.CallbackOn = this.$moment(d.callbackOn).format(
            "DD-MM-YYYY, h:mm a",
          );
        } else {
          y.CallbackOn = "";
        }

        if (d.email != "") {
          y.Email = d.email;
        } else {
          y.Email = "";
        }

        // Default Dialer
        if (d.type == "call" && typeof d.defaultDialer == "undefined") {
          y.defaultDialer = "Phone Dialer";
        } else {
          y.defaultDialer = d.defaultDialer;
        }

        if (d.type == "link") {
          y.defaultDialer = "";
        }

        if (d.list && d.list.teams) {
          y.Team = d.list.teams.name;
        } else {
          y.Team = "";
        }

        if (d.note) {
          y.Notes = d.note;
        } else {
          y.Notes = "";
        }

        if (d.duration) {
          y["duration (sec)"] = Number(d.duration / 1000).toFixed(2);
        }

        if (d.type == "call") {
          if (parseInt(d.Duration) > 0) {
            y.CallStatus = "Connected";
          } else {
            y.CallStatus = "Not Connected";
          }
        } else {
          y.CallStatus = "";
        }

        if (d.timeSpentOnCall) {
          y["TimeSpent (sec)"] = Number(d.timeSpentOnCall / 1000).toFixed(2);
        }

        var statusArray = [];

        if (d.emailSent) {
          statusArray.push("Email");
        }

        if (d.smsSent) {
          statusArray.push("SMS");
        }

        if (d.whatsappSent) {
          statusArray.push("Whatsapp");
        }
        if (d.linkSent) {
          statusArray.push("Link");
        }

        if (d.appSent) {
          statusArray.push("App");
        }

        if (d.called) {
          statusArray.push("Call");
        }

        if (typeof statusArray !== "undefined" && statusArray.length > 0) {
          y.Status = statusArray.join();
        } else {
          y.Status = "";
        }

        if (typeof d.template == "undefined" || d.template.title == "") {
          y.Template = "";
        } else {
          y.Template = d.template.title;
        }

        if (
          typeof d.truecallerCallRecording == "undefined" ||
          d.truecallerCallRecording.name == ""
        ) {
          y.CallRecordingPath = "";
        } else {
          if (typeof d.truecallerCallRecording.name != "undefined") {
            y.CallRecordingPath = `${this.$store.state.ENDPOINT}/storages/get/${d.truecallerCallRecording.name}?access_token=${this.$store.getters.user.token}`;
          } else {
            y.CallRecordingPath = "";
          }
        }

        if (
          typeof d.telecmi != "undefined" &&
          (d.defaultDialer == "Telecmi" ||
            d.defaultDialer == "Telecmi-Linphone")
        ) {
          if (typeof d.telecmi.filename != "undefined") {
            var url = `https://rest.telecmi.com/v2/play?appid=${self.telecmiIntegration.config.appid}&secret=${self.telecmiIntegration.config.secret}&file=${d.telecmi.filename}`;
            y.CallRecordingPath = url;
          } else {
            y.CallRecordingPath = "";
          }
        }

        if (
          typeof d.exotel != "undefined" &&
          d.defaultDialer == "Phone Dialer with Rec"
        ) {
          if (typeof d.exotel.recordingUrl != "undefined") {
            var url2 = d.exotel.recordingUrl;
            console.log("url2", url2);
            y.CallRecordingPath = url2;
          } else {
            y.CallRecordingPath = "";
          }
        }

        if (typeof d["customFields"] != "undefined") {
          if (Array.isArray(d["customFields"])) {
            //CustomField Set For Array
            var customFields = d["customFields"];

            for (var each2 in customFields) {
              var c = customFields[each2];

              y["Custom : " + c.name] = c.value;
            }
          } else {
            //CustomField Set For Object
            for (var each in self.fieldList) {
              if (
                typeof d["customFields"] != "undefined" &&
                d["customFields"][self.fieldList[each].name]
              ) {
                var customVal = d["customFields"][self.fieldList[each].name];

                if (customVal) {
                  if (Array.isArray(customVal)) {
                    y[self.fieldList[each].name] = customVal.toString();
                  } else {
                    if (self.fieldList[each].type == "date") {
                      y[self.fieldList[each].name] = this.$moment(customVal)
                        .format()
                        .substr(0, 10);
                    } else {
                      y[self.fieldList[each].name] = customVal;
                    }
                  }
                } else {
                  y[self.fieldList[each].name] = "";
                }
              }
            }
          }
        }

        return y;
      });

      const newBook = XLSX.utils.book_new();
      const newSheet = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(newBook, newSheet, "Sheet1");
      XLSX.writeFile(newBook, `report_${new Date().getTime()}.xlsx`);
    },

    /** GET THE REQUIRED DATA */
    // getting all the logs depending on the selected user, list and daterange, limit and skip
    async fetchLogs(limit, skip) {
      try {
        this.loading = true;
        this.loadingText = "Loading... Please wait";

        this.snackbar = false;
        this.statistics = [];
        var users = [];
        var lists = [];
        var dispos = [];

        //Get lists
        lists = this.getListIds();

        //Get users
        users = this.getUserIds();

        if (this.dates.length < 1) {
          this.dates = [
            moment()
              .subtract(7, "days")
              .format("YYYY-MM-DD"),
            moment().format("YYYY-MM-DD"),
          ];
        }

        if (limit == undefined || limit == undefined) {
          limit = 5;
          skip = 0;
        }

        // dispo variable
        if (_.includes(this.selectedDispos, "All Dispos")) {
          // dispos = _.map(this.dispos, "title");
          dispos = [];
        } else {
          dispos = this.selectedDispos;
        }

        //final dispos
        dispos = _.without(dispos, "All Dispos");

        // Check Search String null
        if (this.search == undefined || this.search == null) {
          this.search = "";
        }
        var res = await this.$http.post(this.ENDPOINT + "/logs/byFilter", {
          search: this.search,
          field: this.searchField,
          range: this.dates,
          lists: lists,
          users: users,
          dispos: dispos,
          limit: limit,
          skip: skip,
          count: false,
        });

        var response = [];
        response = _.without(
          _.map(res.body.data, (r) => {
            if (r.calledOn != undefined) {
              r.formatedTime = moment(r.calledOn).format("YYYY-MM-DD hh:mm A");
              r.time = moment(r.calledOn).format("hh:mm A");
              r.date = moment(r.calledOn).format("YYYY-MM-DD");

              // Filter based on call direction
              if (this.selectedCallDirection !== "all") {
                if (r.type === "call") {
                  // For outgoing calls, either direction is 'outgoing' or undefined
                  if (this.selectedCallDirection === "outgoing") {
                    if (r.direction && r.direction !== "outgoing") {
                      return undefined;
                    }
                  }
                  // For incoming calls, direction must be explicitly 'incoming'
                  else if (this.selectedCallDirection === "incoming") {
                    if (r.direction !== "incoming") {
                      return undefined;
                    }
                  }
                }
              }

              if (typeof r.truecallerCallRecording != "undefined") {
                r.recording = r.truecallerCallRecording;
                r.recording.isPlaying = false;
                r.recording.playbackTime = 0;
              } else {
                r.recording = { success: false };
              }

              if (
                typeof r.telecmi != "undefined" &&
                (r.defaultDialer == "Telecmi" ||
                  r.defaultDialer == "Telecmi-Linphone")
              ) {
                if (typeof r.telecmi["filename"] != "undefined") {
                  r.recording = {
                    success: true,
                    filename: r.telecmi.filename,
                    url:
                      "https://s3.ap-south-1.amazonaws.com/telecmi/" +
                      r.telecmi.filename,
                  };

                  r.recording.isPlaying = false;
                  r.recording.playbackTime = 0;
                } else {
                  r.recording = { success: false };
                }
              }

              if (
                typeof r.exotel != "undefined" &&
                r.defaultDialer == "Phone Dialer with Rec"
              ) {
                if (typeof r.exotel["recordingUrl"] != "undefined") {
                  r.recording = {
                    success: true,
                    filename: r.exotel.recordingUrl,
                    url: r.exotel.recordingUrl,
                  };

                  r.recording.isPlaying = false;
                  r.recording.playbackTime = 0;
                } else {
                  r.recording = { success: false };
                }
              }

              // SIP Call Recording - handled by lazy loading in openSipRecording()
              // No need to process sipCallRecording here

              if (r.type == "call" && typeof r.defaultDialer == "undefined") {
                r.defaultDialer = "Phone Dialer";
              }

              if (r.type == "link") {
                r.defaultDialer = "";
              }

              if (typeof r.duration != "undefined" && r.type == "call") {
                var inSecs = r.duration / 1000;

                if (inSecs > 60) {
                  r.Duration = `${Number(inSecs / 60).toFixed(1)} mins`;
                } else {
                  r.Duration = `${Number(inSecs).toFixed(0)}s`;
                }

                if (parseInt(r.duration) > 0) {
                  r.callStatus = "Connected";
                } else {
                  r.callStatus = "Not Connected";
                }
              }

              if (typeof r.timeSpentOnCall != "undefined" && r.type == "call") {
                var timeSpentOnCall = r.timeSpentOnCall / 1000;

                if (timeSpentOnCall > 60) {
                  r.TimeSpent = `${Number(timeSpentOnCall / 60).toFixed(
                    1,
                  )} mins`;
                } else {
                  r.TimeSpent = `${Number(timeSpentOnCall).toFixed(0)}s`;
                }
                if (
                  typeof r.duration != "undefined" ||
                  r.duration === "" ||
                  r.duration === "0s"
                ) {
                  r.duration = r.TimeSpent;
                }
              }

              if (r.type != "call") {
                r.dispo = "";
                r.note = "";
              }

              // accounts

              var account = _.find(this.allUsers, (user) => {
                return r.accountsId == user.id;
              });

              if (account) {
                r.accounts = account;
              }

              //lists
              var list = _.find(this.lists, (list) => {
                return r.listId == list.id;
              });
              if (list) {
                r.list = list;
              }

              //templates
              var template = _.find(this.templates, (template) => {
                return r.templateId == template.id;
              });
              if (template) {
                r.template = template;
              }
            }

            try {
              var output = _.mapValues(r.customFields, (value, key) => {
                var fieldDef = _.find(this.fieldList, (f) => {
                  return f.name == key;
                });

                if (!fieldDef) return value;

                switch (fieldDef.type) {
                  case "date":
                    if (this.$moment(value).isValid())
                      value = this.$moment(value)
                        .format()
                        .substr(0, 10);
                    else value = "";
                    break;
                  case "text":
                    value = value.toString().trim();
                    break;
                  case "number":
                    value = Number(value);
                    break;
                  case "mcq":
                    if (!_.includes(fieldDef.mcqOptions, value)) value = "";
                    break;
                  // case "checkbox":
                  //   if (!_.includes(fieldDef.checkboxOptions, value)) value = "";
                  //   break;
                }
                return value;
              });
              r.customFields = output;
            } catch (e) {
              console.log(e);
            }

            return r;
          }),
          undefined,
        );
        response = _.orderBy(response, "calledOn", "desc");

        this.totalConnectedCall = 0;
        this.totalNotConnectedCall = 0;

        var pieResponse = "";

        pieResponse = _.map(response, (d) => {
          if (d.type == "call") {
            if (parseInt(d.Duration) > 0) {
              this.totalConnectedCall++;
            } else {
              this.totalNotConnectedCall++;
            }
          }
        });

        //Pie Chart
        this.pieChartData = {
          labels: [
            `Connected Calls (${this.totalConnectedCall})`,
            `Not Connected Calls (${this.totalNotConnectedCall})`,
          ],
          datasets: [
            {
              backgroundColor: ["#2ecc71", "#FF6384"],
              data: [this.totalConnectedCall, this.totalNotConnectedCall],
            },
          ],
        };

        console.log("response 13124214325423", response);

        this.loading = false;
        this.loadingText = "";
        return response;
      } catch (e) {
        console.log(e);
        this.loading = false;
        this.loadingText = "Something went wrong while fetching call logs";
      }
    },
    /** Total logs count [ used for pagination ] */
    async fetchCount() {
      // this.loading = true;
      // this.loadingText = "Loading... Please wait";

      var users = [];
      var lists = [];
      var dispos = [];

      //Get lists
      lists = this.getListIds();

      //Get lists
      users = this.getUserIds();

      if (this.dates.length < 1) {
        this.dates = [
          moment()
            .subtract(7, "days")
            .format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ];
      }

      // dispo variable
      if (_.includes(this.selectedDispos, "All Dispos")) {
        // dispos = _.map(this.dispos, "title");
        dispos = [];
      } else {
        dispos = this.selectedDispos;
      }

      //final dispos
      dispos = _.without(dispos, "All Dispos");

      // Check Search string null
      if (this.search == undefined || this.search == null) {
        this.search = "";
      }

      this.$http
        .post(this.ENDPOINT + "/logs/byFilter", {
          search: this.search,
          field: this.searchField,
          range: this.dates,
          lists: lists,
          users: users,
          dispos: dispos,
          count: true,
        })
        .then((response) => {
          this.totalRecords = response.body;
          this.pageCount = Math.ceil(this.totalRecords / this.records);
        })
        .catch((e) => {});
    },
    async fetchTemplates() {
      var self = this;

      try {
        var res = await self.$http.get(
          self.$store.state.ENDPOINT +
            "/templates?filter[where][companyId]=" +
            self.$store.getters.user.companyId,
        );

        self.templates = res.body;
      } catch (e) {
        console.log(e);
      }
    },
    async fetchLists() {
      try {
        this.lists = [];
        var filter = {
          where: {
            companyId: this.$store.state.user.companyId,
          }
        };

        if (this.$store.getters.user.role.toLowerCase() != "manager") {
          filter.where["teamsId"] = {
            inq: _.without(_.map(this.teams, "id"), "allTeams"),
          };
        }

        for (let i of this.fieldList) {
          this.allHeaders.push({
            text: i.name,
            align: "left",
            sortable: false,
            value: "customFields." + i.name,
          });
        }

        var res = await this.$http.get(
          `${this.ENDPOINT}/lists/fetch-list-v2?filter=${encodeURIComponent(
            JSON.stringify(filter),
          )}`,
        );
        this.lists = res.body;

        if (
          typeof this.user.permissions.canAccessList != "undefined" &&
          this.user.permissions.canAccessList === false
        ) {
          this.lists = _.filter(this.lists, (list) => {
            return _.find(list.accounts, (account) => {
              return account.id == this.user.id;
            });
          });
        }

        this.lists.unshift({
          id: "allLists",
          name: "All Lists",
        });

        if (this.selectedList.length < 1) {
          this.selectedList.push(this.lists[0].id);
        }
      } catch (e) {
        console.log(e);
      }
    },

    getTeamIds() {
      var ids = [];

      if (this.selectedTeam == "allTeams") {
        ids = _.map(this.teams, "id");
      } else {
        ids.push(this.selectedTeam);
      }

      ids = _.without(ids, "allTeams");
      return ids;
    },

    // Fetch Accounts
    async fetchAccounts() {
      this.allUsers = [];

      try {
        var teams = [];
        teams = await this.getTeamIds();

        var response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/accounts/fetch-v2`,
          {
            teams: teams,
          },
        );
        this.allUsers = response.body;
        if (this.user.role == "Manager" || this.user.role == "Teammanager") {
          this.allUsers.unshift({
            id: "allMembers",
            name: "All Members",
          });
        }
        if (this.selectedUser.length < 1) {
          this.selectedUser.push(this.allUsers[0].id);
        }
      } catch (e) {
        if (e.body.error && e.body.error.message)
          return this.$swal({ type: "error", text: e.body.error.message });
        else
          return this.$swal({
            type: "error",
            text: "Error while fetching accounts",
          });
      }
    },

    async fetchDispos() {
      try {
        this.dispos = [];

        var res = await this.$http.get(
          this.ENDPOINT +
            "/dispos?filter[where][companyId]=" +
            this.user.companyId,
        );
        this.dispos = res.body;
        if (this.dispos.length > 0) {
          this.dispos.unshift({
            title: "All Dispos",
          });
        }

        if (this.selectedDispos.length < 1) {
          this.selectedDispos.push(this.dispos[0].title);
        }
      } catch (e) {
        console.log("dispo related Isususes", e);
      }
    },

    async fetchDeletedListIds() {
      try {
        const start = "2020-01-01";
        const end = this.$moment().format("YYYY-MM-DD");
        const response = await this.$http.post(
          `${this.ENDPOINT}/audits/fetch`,
          {
            users: [],
            action: "deleted",
            model: "list",
            start: start,
            end: end,
            limit: 1000,
            skip: 0,
          }
        );
        const auditData = response.body.data || [];
        this.deletedListIds = auditData
          .map((d) => d.target && d.target.id)
          .filter(Boolean);
      } catch (e) {
        console.error("Error fetching deleted list IDs:", e);
      }
    },

    /** GRAPH REPORT */
    fetchGraphReports() {
      this.preloader = true;
      this.statistics = [];

      var users = [];
      var lists = [];
      var dispos = [];

      //Get lists
      lists = this.getListIds();

      //Get lists
      users = this.getUserIds();

      if (this.dates.length < 1) {
        this.dates = [
          moment()
            .subtract(7, "days")
            .format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ];
      }

      if (this.dates.length < 1) {
        this.dates = [
          moment()
            .subtract(7, "days")
            .format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ];
      }

      // dispo variable
      if (_.includes(this.selectedDispos, "All Dispos")) {
        // dispos = _.map(this.dispos, "title");
        dispos = [];
      } else {
        dispos = this.selectedDispos;
      }

      //final dispos
      dispos = _.without(dispos, "All Dispos");

      this.$http
        .post(this.ENDPOINT + "/logs/v2/graph", {
          range: this.dates,
          lists: lists,
          users: users,
          dispos: dispos,
        })
        .then((response) => {
          response = response.body;

          response.forEach((stat) => {
            this.statistics.push({
              _id: stat._id,
              duration: stat.duration,
              count: stat.count,
            });
            for (var y in stat.status) {
              let exists = _.find(this.graphData, (s) => {
                return s._id == stat.status[y]["_id"];
              });
              let index = _.indexOf(this.graphData, exists);
              if (index > -1) {
                this.graphData[index].count =
                  this.graphData[index].count + stat.status[y]["count"];
                continue;
              }
              this.graphData.push({
                _id: stat.status[y]["_id"],
                count: stat.status[y]["count"],
              });
            }
          });
          this.fillBarData();
          this.fillData();

          this.preloader = false;
        })
        .catch((e) => {
          this.preloader = false;
        });
    },

    fillBarData() {
      this.loadChart = false;
      var counter = new Object();
      this.totalCallCount = 0;
      this.totalCallDuration = 0;

      this.statistics = _.map(this.statistics, (d) => {
        var obj = {};
        obj.account = _.find(this.allUsers, (a) => {
          return a.id == d._id.accountsId;
        });
        obj.accountsId = d._id.accountsId;
        obj.count = d.count;

        if (d.duration != undefined) {
          d.duration = d.duration / 1000;
          obj.duration = Number(d.duration / 60);
          this.totalCallDuration += obj.duration;
        }

        if (obj.account != undefined) {
          counter[obj.account.name] = d.count;
        }
        this.totalCallCount += obj.count;

        return obj;
      });
      this.totalCallDuration = `${this.totalCallDuration.toFixed(1)} mins`;

      //Bar Chart
      this.barChartData = {
        datasets: [
          {
            backgroundColor: "#4cd964",
            data: [..._.map(counter)],
          },
        ],
        // These labels appear in the legend and in the tooltips when hovering different arcs
        labels: Object.keys(counter),
      };

      this.loadChart = true;
    },
    async fillData() {
      const self = this;
      self.datacollection = {};
      self.loadChart = false;
      self.labelShown = false;

      var colors = [];
      this.graphData = _.orderBy(this.graphData, ["count"], ["desc"]);
      if (this.graphData.length > 10) {
        var graphNew = this.graphData.slice(0, 10);
        var graphOther = {
          _id: "Others",
          count: 0,
        };
        for (let i = 10; i < this.graphData.length; i++) {
          graphOther.count += this.graphData[i].count;
        }
        this.graphData = graphNew.concat(graphOther);
      }
      _.each(this.graphData, (val) => {
        val = _.find(self.dispoList, (d) => {
          return d.title == val._id;
        });

        if (val == undefined) {
          colors.push("#d9d9d9");
        } else {
          colors.push(val.hex);
        }
      });
      this.labelShown = false;

      var labels = _.map(
        this.graphData,
        (item) => `${item._id} (${item.count})`,
      );

      // //Chart
      self.datacollection = {
        datasets: [
          {
            backgroundColor: colors,
            data: _.map(this.graphData, "count"),
            borderWidth: 0,
          },
        ],
        // These labels appear in the legend and in the tooltips when hovering different arcs
        labels: labels,
      };

      self.labelShown = true;

      await self.$nextTick((err) => {
        self.loadChart = true;
        return;
      });
    },

    /** EXPORT (CSV) */
    async exportReport() {
      var self = this;
      this.loader1 = true;
      this.exportProgress = 0;

      try {
        var dispos = [];
        var lists = this.getListIds();
        var users = this.getUserIds();

        if (this.dates.length < 1) {
          this.dates = [
            moment().subtract(7, "days").format("YYYY-MM-DD"),
            moment().format("YYYY-MM-DD"),
          ];
        }

        if (_.includes(this.selectedDispos, "All Dispos")) {
          dispos = [];
        } else {
          dispos = this.selectedDispos;
        }
        dispos = _.without(dispos, "All Dispos");


        if (this.totalRecords < 5000) {
          var maxCount = 2500;
          var data = [], res;
          var totalBatches = Math.ceil(this.totalRecords / maxCount);
          this.totalBatches = totalBatches;
          var limit = 0, skip = 0;
          var processedRecords = 0;

          for (let currentBatch = 1; currentBatch <= totalBatches; currentBatch++) {
            this.currentBatch = currentBatch;
            skip += limit;
            limit = maxCount;
            res = await this.$http.post(`${this.ENDPOINT}/logs/byFilter`, {
              companyId: this.$store.getters.user.companyId,
              range: this.dates,
              lists: lists,
              users: users,
              dispos: dispos,
              limit: limit,
              skip: skip,
              count: false,
            });
            data = data.concat(res.body.data);
            processedRecords += res.body.data.length;
            this.exportProgress = Math.round((processedRecords / this.totalRecords) * 100);
          }

          this.exportProgress = 100;

          data = _.map(data, (r) => {
            var account = _.find(this.allUsers, (user) => r.accountsId == user.id);
            if (account) r.accounts = account;
            var list = _.find(this.lists, (list) => r.listId == list.id);
            if (list) r.list = list;
            var template = _.find(this.templates, (template) => r.templateId == template.id);
            if (template) r.template = template;
            return r;
          });

          var exportData = _.orderBy(data, "time", "desc");
          if (exportData.length < 1) {
            this.loader1 = false;
            return this.$swal({ type: "warning", text: "No records to export" });
          }

          exportData = _.map(exportData, (d) => {
            var y = { Phone: d.phone ? d.phone.toString() : "" };
            y.Name = d.name || "";
            y.Company = d.companyName || "";
            y.Date = d.calledOn ? this.$moment(d.calledOn).format("YYYY-MM-DD") : "";
            y.Time = d.calledOn ? this.$moment(d.calledOn).format("hh:mm a") : "";
            y.Leadscore = d.leadScore || "";
            y.Remarks = d.remarks || "";
            y.Extra = d.extra || "";
            y.CalledBy = d.accounts && d.accounts.name ? d.accounts.name : "";
            y.Direction = d.direction || "outgoing";
            y.List = d.list && d.list.name ? d.list.name : "";
            y.Disposition = d.dispo || "";
            y.CallbackOn = d.callbackOn ? this.$moment(d.callbackOn).format("DD-MM-YYYY, h:mm a") : "";
            y.Email = d.email || "";
            y.defaultDialer = d.type === "call" && d.defaultDialer === undefined ? "Phone Dialer" : d.defaultDialer || "";
            y.Team = d.list && d.list.teams ? d.list.teams.name : "";
            y.Notes = d.note || "";
            y["duration (sec)"] = d.duration ? Number(d.duration / 1000).toFixed(2) : "";
            y.CallStatus = d.type === "call" ? parseInt(d.duration) > 0 ? "Connected" : "Not Connected" : "";
            y["TimeSpent (sec)"] = d.timeSpentOnCall ? Number(d.timeSpentOnCall / 1000).toFixed(2) : "";
            var statusArray = [];
            if (d.emailSent) statusArray.push("Email");
            if (d.smsSent) statusArray.push("SMS");
            if (d.whatsappSent) statusArray.push("Whatsapp");
            if (d.linkSent) statusArray.push("Link");
            if (d.appSent) statusArray.push("App");
            if (d.called) statusArray.push("Call");
            y.Status = statusArray.length > 0 ? statusArray.join() : "";
            y.Template = d.template && d.template.title ? d.template.title : "";
            if (d.truecallerCallRecording && d.truecallerCallRecording.name && typeof d.truecallerCallRecording.name !== "undefined") {
              y.CallRecordingPath = `${this.$store.state.ENDPOINT}/storages/get/${d.truecallerCallRecording.name}?access_token=${this.$store.getters.user.token}`;
            } else {
              y.CallRecordingPath = "";
            }
            if (d.telecmi && (d.defaultDialer == "Telecmi" || d.defaultDialer == "Telecmi-Linphone")) {
              if (typeof d.telecmi.filename != "undefined") {
                y.CallRecordingPath = `https://rest.telecmi.com/v2/play?appid=${self.telecmiIntegration.config.appid}&secret=${self.telecmiIntegration.config.secret}&file=${d.telecmi.filename}`;
              } else {
                y.CallRecordingPath = "";
              }
            }
            if (d.exotel && d.defaultDialer == "Phone Dialer with Rec") {
              if (typeof d.exotel.recordingUrl != "undefined") {
                y.CallRecordingPath = d.exotel.godialRecordingUrl;
              } else {
                y.CallRecordingPath = "";
              }
            }
            if (typeof d["customFields"] != "undefined") {
              if (Array.isArray(d["customFields"])) {
                for (var each2 in d["customFields"]) {
                  var c = d["customFields"][each2];
                  y["Custom : " + c.name] = c.value;
                }
              } else {
                for (var each in self.fieldList) {
                  if (typeof d["customFields"] != "undefined" && d["customFields"][self.fieldList[each].name]) {
                    var customVal = d["customFields"][self.fieldList[each].name];
                    if (customVal) {
                      if (Array.isArray(customVal)) {
                        y[self.fieldList[each].name] = customVal.toString();
                      } else {
                        if (self.fieldList[each].type == "date") {
                          y[self.fieldList[each].name] = this.$moment(customVal).format().substr(0, 10);
                        } else {
                          y[self.fieldList[each].name] = customVal;
                        }
                      }
                    } else {
                      y[self.fieldList[each].name] = "";
                    }
                  }
                }
              }
            }
            return y;
          });

          const newBook = XLSX.utils.book_new();
          const newSheet = XLSX.utils.json_to_sheet(exportData);
          XLSX.utils.book_append_sheet(newBook, newSheet, "Sheet1");
          this.exportProgress = 130;
          XLSX.writeFile(newBook, `report_${new Date().getTime()}.xlsx`);
          this.loader1 = false;
          fb.log("report_export");

        } else {
          var response = await self.$http.post(
            `${self.ENDPOINT}/logs/exportv2`,
            {
              companyId: this.$store.getters.user.companyId,
              range: this.dates,
              lists: lists,
              users: users,
              dispos: dispos,
            }
          );

          const {
            batchId,
            fileName,
            totalBatches,
            totalRecords,
          } = response.body;

          let batchStatus = await this.fetchDetails(batchId);

          if (batchStatus.status == "complete") {
            var swalMessage = "Your file is ready to download";
            var swalType = "success";
            if (batchStatus.partialFailure) {
              swalMessage = `Your file is ready but ${batchStatus.failedBatches} batch(es) failed. Some records may be missing. Download anyway or retry?`;
              swalType = "warning";
            }
            await self
              .$swal({
                type: swalType,
                text: swalMessage,
                showCancelButton: true,
                confirmButtonColor: "#31a848",
                cancelButtonColor: "#F44336",
                confirmButtonText: batchStatus.partialFailure ? "Download Anyway" : "Download",
                cancelButtonText: batchStatus.partialFailure ? "Retry" : "Cancel",
              })
              .then(async (yes) => {
                if (yes.value) {
                  try {
                    var downloadResp = await self.$http.post(
                      `${self.ENDPOINT}/contacts/downloadExportBigData`,
                      { fileName: fileName },
                    );
                    if (downloadResp.body && downloadResp.body.url) {
                      self.downloadFileFromUrl(
                        downloadResp.body.url,
                        `report_${new Date().getTime()}.csv`,
                      );
                    } else {
                      self.$swal({
                        type: "error",
                        text: "Export file not found. Please try again.",
                      });
                    }
                  } catch (error) {
                    self.$swal({
                      type: "error",
                      text:
                        error.body && error.body.error
                          ? error.body.error.message
                          : "Something went wrong! Please try again.",
                    });
                  }
                } else {
                  if (batchStatus.partialFailure) {
                    self.loader1 = false;
                    self.exportProgress = 0;
                    self.exportReport();
                  }
                }
              });
          }
          self.loader1 = false;
          fb.log("report_export");
        }
      } catch (e) {
        console.log(e);
        this.loader1 = false;
        return this.$swal({
          type: "error",
          text: e.body && e.body.error ? e.body.error.message : "Connection error. Try again later.",
        });
      }
    },
    fetchDetails(batchId) {
      return new Promise((resolve, reject) => {
        const checkStatus = () => {
          this.$http
            .get(`${this.ENDPOINT}/exportBatchstatuses/${batchId}`)
            .then((response) => {
              if (response.body.status === "complete") {
                this.exportProgress = 100;
                if (response.body.failedBatches && response.body.failedBatches > 0) {
                  resolve({
                    ...response.body,
                    partialFailure: true,
                    failedBatches: response.body.failedBatches,
                  });
                } else {
                  resolve(response.body);
                }
              } else if (response.body.status === "failed") {
                this.$swal({
                  type: "error",
                  text: response.body.error,
                }).then(() => {
                  this.loader1 = false;
                  this.exportProgress = 0;
                  reject(new Error(response.body.error));
                });
              } else {
                const { completed, totalBatches } = response.body;
                this.currentBatch = completed || 0;
                this.totalBatches = totalBatches || 0;
                
                const percentage = Math.round((completed / totalBatches) * 100);
                if (response.body.status === "combining") {
                  this.exportProgress = 99;
                } else if (completed == totalBatches) {
                  this.exportProgress = 99;
                } else {
                  this.exportProgress = percentage;
                }
                setTimeout(checkStatus, 3000);
              }
            })
            .catch((error) => {
              console.error("Failed to fetch details:", error);
              reject(error);
            });
        };
        checkStatus();
      });
    },
    downloadFileFromUrl(fileUrl, downloadName) {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", downloadName || "report_export.csv");
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.reports-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .reports-container {
  background-color: #121212 !important;
}

.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px;
  padding: 32px;
  color: white;
  box-shadow: 0 4px 20px rgba(55, 207, 55, 0.3);
}

.header-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: white;
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.feature-pills {
  margin-top: 24px;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  height: 100%;
  min-height: 48px;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

/* Tablet view fix for feature pills */
@media (max-width: 960px) {
  .feature-pill {
    padding: 10px 12px;
    font-size: 12px;
  }

  .feature-pills {
    margin-top: 16px;
  }
}

.modern-card {
  background: var(--v-background-base, white);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.customBorderRadius {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.card-header-green {
  height: 4px;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
}

.chart-card {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
}

.theme--dark .chart-card {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

/* Fix filter dropdowns vertical centering */
.reports-container ::v-deep .v-autocomplete .v-input__control,
.reports-container ::v-deep .v-select .v-input__control,
.reports-container ::v-deep .v-text-field .v-input__control {
  min-height: 48px !important;
}

.reports-container ::v-deep .v-autocomplete .v-input__slot,
.reports-container ::v-deep .v-select .v-input__slot,
.reports-container ::v-deep .v-text-field .v-input__slot {
  min-height: 48px !important;
  align-items: center !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.reports-container ::v-deep .v-autocomplete .v-select__selections,
.reports-container ::v-deep .v-select .v-select__selections {
  min-height: 48px !important;
  padding: 8px 0 !important;
  align-items: center !important;
  flex-wrap: nowrap !important;
}

.reports-container ::v-deep .v-autocomplete .v-select__selections .v-chip,
.reports-container ::v-deep .v-select .v-select__selections .v-chip {
  margin: 0 4px 0 0 !important;
  max-width: 100px !important;
}

.reports-container
  ::v-deep
  .v-autocomplete
  .v-select__selections
  .v-chip
  .v-chip__content,
.reports-container
  ::v-deep
  .v-select
  .v-select__selections
  .v-chip
  .v-chip__content {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  display: block !important;
  max-width: 100% !important;
}

.reports-container ::v-deep .v-autocomplete .v-select__selections .v-chip span,
.reports-container ::v-deep .v-select .v-select__selections .v-chip span {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  display: block !important;
  margin-top: -1px !important;
}

/* Center the label when not focused/dirty */
.reports-container ::v-deep .v-autocomplete .v-label,
.reports-container ::v-deep .v-select .v-label,
.reports-container ::v-deep .v-text-field .v-label {
  top: 50% !important;
  transform: translateY(-50%) !important;
}

/* Move label to top when focused or has value */
.reports-container ::v-deep .v-input--is-label-active .v-label,
.reports-container ::v-deep .v-input--is-focused .v-label,
.reports-container ::v-deep .v-input--is-dirty .v-label {
  top: 0 !important;
  transform: translateY(-18px) scale(0.75) !important;
}

@media (max-width: 960px) {
  .header-icon-wrapper {
    width: 56px;
    height: 56px;
  }

  .page-title {
    font-size: 28px;
  }
}

/* Table Scroll Controls */
.scroll-controls {
  margin-right: 8px;
}

.scroll-btn {
  border: 1px solid #e0e0e0;
  border-radius: 8px !important;
  background: #fff !important;
  transition: all 0.2s ease;
}

.scroll-btn:hover {
  border-color: #37cf37;
  background: rgba(55, 207, 55, 0.05) !important;
}

.theme--dark .scroll-btn {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

.theme--dark .scroll-btn:hover {
  background: rgba(55, 207, 55, 0.1) !important;
}

/* Classic / Modern view toggle */
.reports-action-bar {
  display: flex;
  align-items: center;
  justify-content: center;
}
.classic-view-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: #333833;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 10px;
  height: 36px;
  justify-content: center;
  padding: 0 4px;
  white-space: nowrap;
}
.classic-view-toggle:focus-visible {
  border-radius: 999px;
  box-shadow: 0 0 0 3px rgba(25, 155, 56, 0.2);
  outline: none;
}
.classic-view-toggle:hover .classic-view-toggle-label {
  color: #199b38;
}
.classic-view-toggle-label {
  color: #6f746e;
  transition: color 0.18s ease;
}
.classic-view-classic-label {
  color: #199b38;
}
.classic-view-switch {
  background: #cfd4cd;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
  display: inline-flex;
  height: 20px;
  padding: 2px;
  transition: background 0.18s ease;
  width: 40px;
}
.classic-view-switch-thumb {
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.22);
  height: 16px;
  transition: transform 0.18s ease;
  width: 16px;
}
.classic-view-toggle-active .classic-view-classic-label {
  color: #6f746e;
}
.classic-view-toggle-active .classic-view-modern-label {
  color: #199b38;
}
.classic-view-toggle-active .classic-view-switch {
  background: #199b38;
}
.classic-view-toggle-active .classic-view-switch-thumb {
  transform: translateX(18px);
}
</style>
